import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal, Tabs, Button, Input, InputNumber, Checkbox, Spin, Empty,
  Tooltip, Tag, message, Collapse, Divider, Alert,
} from "antd";
import {
  CopyOutlined, SendOutlined, LinkOutlined, ReloadOutlined,
  CheckCircleOutlined, ClockCircleOutlined, HistoryOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useSocket } from "../contexts/SocketContext";
import axios from "axios";
import dayjs from "dayjs";
import { API_DOMAIN } from "../constants";
import network_logo from "../../assets/images/network_logo.jpg";
import mamo_logo from "../../assets/images/mamo_logo.jpg";

// ── Cost tier fetch (module-level cache, independent of PriceUpdateDrawer) ───

let _cachedCosts = null;
let _costsFetch = null;
function fetchCostPricesOnce() {
  if (_cachedCosts?.length) return Promise.resolve(_cachedCosts);
  if (_costsFetch) return _costsFetch;
  _costsFetch = axios
    .post(`${API_DOMAIN}/whatsapp/get_costprices`, {}, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    })
    .then(res => {
      const data = Array.isArray(res.data?.data ?? res.data) ? (res.data?.data ?? res.data) : [];
      if (data.length) _cachedCosts = data;
      _costsFetch = null;
      return data;
    })
    .catch(() => { _costsFetch = null; return []; });
  return _costsFetch;
}

function getTier(costPrices, price) {
  return costPrices.find(t => price >= t.MinPrice && price <= t.MaxPrice) ?? null;
}

// ── Pricing helpers ───────────────────────────────────────────────────────────

const DEFAULT_VAT_PCT = 5;
const NETWORK_FEE_PCT = 2.5;
const MAMO_FEE_PCT = 2.5;

function computeDelivery(parts, costPrices) {
  if (!costPrices.length) return 0;
  const totalCost = parts.reduce((s, p) => {
    if (!p.selected_quote_id) return s;
    const q = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
    return s + Number(q?.quoted_price || 0);
  }, 0);
  if (totalCost === 0) return 0;
  const tier = getTier(costPrices, totalCost);
  return tier ? Number(tier.DeliveryCost) : 0;
}

function calcTotals({ partsTotal, delivery, vatPct, feePct, discount }) {
  const vatAmount = Math.round((partsTotal + Number(delivery)) * (vatPct / 100) * 100) / 100;
  const processingBase = partsTotal + Number(delivery) + vatAmount;
  const processingFeeAmount = Math.round(processingBase * (feePct / 100) * 100) / 100;
  const finalPayable = Math.max(0, processingBase + processingFeeAmount - Number(discount));
  return { vatAmount, processingFeeAmount, finalPayable };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PricingSnapshot({ selectedParts, partsTotal, delivery, setDelivery, vatPct, setVatPct, feePct, setFeePct, discount, setDiscount, discountReason, setDiscountReason }) {
  const { vatAmount, processingFeeAmount, finalPayable } = calcTotals({ partsTotal, delivery, vatPct, feePct, discount });

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Parts breakdown */}
      <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
        <div style={{ background: "#fafafa", padding: "6px 12px", fontWeight: 600, fontSize: 12, borderBottom: "1px solid #f0f0f0" }}>
          Parts Breakdown
        </div>
        {selectedParts.length === 0 ? (
          <div style={{ padding: "12px", textAlign: "center", color: "#bfbfbf", fontSize: 12 }}>No parts selected</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={{ padding: "6px 12px", textAlign: "left", fontWeight: 600, color: "#595959", borderBottom: "1px solid #f0f0f0" }}>Part</th>
                <th style={{ padding: "6px 12px", textAlign: "right", fontWeight: 600, color: "#595959", borderBottom: "1px solid #f0f0f0" }}>Selling Price</th>
              </tr>
            </thead>
            <tbody>
              {selectedParts.map((p, i) => (
                <tr key={p.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td style={{ padding: "6px 12px", color: "#262626" }}>{p.part_name || `Part #${p.id}`}</td>
                  <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 600 }}>AED {Number(p.selling_price).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Fee inputs + totals */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Delivery (AED)</div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            precision={2}
            value={delivery}
            onChange={v => setDelivery(v ?? 0)}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>VAT %</div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            max={100}
            precision={2}
            value={vatPct}
            onChange={v => setVatPct(v ?? 0)}
            addonAfter="%"
          />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Processing Fee %</div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            max={10}
            precision={2}
            value={feePct}
            onChange={v => setFeePct(v ?? 0)}
            addonAfter="%"
          />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Discount (AED)</div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            precision={2}
            value={discount}
            onChange={v => setDiscount(v ?? 0)}
          />
        </div>
      </div>
      {Number(discount) > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Discount Reason</div>
          <Input
            placeholder="Reason for discount"
            value={discountReason}
            onChange={e => setDiscountReason(e.target.value)}
          />
        </div>
      )}

      {/* Summary */}
      <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 6, padding: "10px 14px" }}>
        {[
          ["Parts Total", partsTotal.toFixed(2)],
          ["Delivery", Number(delivery).toFixed(2)],
          [`VAT (${vatPct}%)`, vatAmount.toFixed(2)],
          [`Processing Fee (${feePct}%)`, processingFeeAmount.toFixed(2)],
          ...(Number(discount) > 0 ? [["Discount", `-${Number(discount).toFixed(2)}`]] : []),
        ].map(([label, val]) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#595959", marginBottom: 4 }}>
            <span>{label}</span>
            <span>AED {val}</span>
          </div>
        ))}
        <Divider style={{ margin: "6px 0" }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 14, color: "#389e0d" }}>
          <span>Final Payable</span>
          <span>AED {finalPayable.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function LinkDisplay({ link, paymentId, userId, customerPhone, sentAt, onSent }) {
  const [sending, setSending] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(link).then(
      () => message.success("Link copied"),
      () => message.error("Failed to copy")
    );
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API_DOMAIN}/payments/${paymentId}/send-link`,
        { userId },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      message.success("Payment link sent to customer via WATI");
      onSent?.(res.data.data.payment);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "Failed to send link");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ border: "1px solid #b7eb8f", borderRadius: 6, background: "#f6ffed", padding: "10px 14px", marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: "#8c8c8c" }}>Payment Link</span>
        {sentAt ? (
          <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 10, margin: 0 }}>
            Sent {dayjs.utc(sentAt).format("DD MMM · hh:mm A")}
          </Tag>
        ) : (
          <Tag color="orange" icon={<ClockCircleOutlined />} style={{ fontSize: 10, margin: 0 }}>
            Not sent yet
          </Tag>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <Tooltip title="Click to copy">
          <div
            onClick={handleCopy}
            style={{ flex: 1, fontSize: 12, color: "#1677ff", wordBreak: "break-all", cursor: "pointer" }}
          >
            {link}
          </div>
        </Tooltip>
        <Button size="small" icon={<CopyOutlined />} onClick={handleCopy}>Copy</Button>
      </div>
      {customerPhone && (
        <Button
          type={sentAt ? "default" : "primary"}
          icon={<SendOutlined />}
          loading={sending}
          onClick={handleSend}
          block
        >
          {sentAt ? "Re-send to Customer on WATI" : "Send to Customer on WATI"}
        </Button>
      )}
    </div>
  );
}

function PaymentHistory({ history, loading, customerPhone, customerName }) {
  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><Spin /></div>;
  }
  if (!history.length) {
    return <Empty description="No payments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 16 }} />;
  }

  const statusColor = (s) => {
    if (s === "paid") return "green";
    if (s === "failed") return "red";
    if (s === "expired") return "default";
    return "orange";
  };

  return (
    <Collapse
      size="small"
      accordion
      defaultActiveKey={[String(history[0]?.id)]}
      style={{ marginTop: 8 }}
      items={history.map((row, idx) => ({
        key: String(row.id),
        label: (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 600, fontSize: 12 }}>
              #{history.length - idx} — AED {Number(row.final_payable ?? 0).toFixed(2)}
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Tag color={statusColor(row.payment_status)} style={{ fontSize: 10, margin: 0 }}>
                {row.payment_status || "pending"}
              </Tag>
              <span style={{ fontSize: 11, color: "#8c8c8c" }}>
                {row.created_at ? dayjs.utc(row.created_at).format("DD MMM · hh:mm A") : ""}
              </span>
            </div>
          </div>
        ),
        children: (
          <div style={{ fontSize: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: "#8c8c8c" }}>Provider</span>
              <span style={{ fontWeight: 600 }}>{row.provider_name || "—"}</span>
            </div>
            {row.payment_link_url && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ color: "#8c8c8c", marginBottom: 3 }}>Link</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div
                    onClick={() => { navigator.clipboard.writeText(row.payment_link_url); message.success("Copied"); }}
                    style={{ flex: 1, fontSize: 11, color: "#1677ff", wordBreak: "break-all", cursor: "pointer" }}
                  >
                    {row.payment_link_url}
                  </div>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => { navigator.clipboard.writeText(row.payment_link_url); message.success("Copied"); }}
                  />
                </div>
              </div>
            )}
            {row.link_sent_at && (
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Sent at {dayjs.utc(row.link_sent_at).format("DD MMM · hh:mm A")}
              </div>
            )}
          </div>
        ),
      }))}
    />
  );
}

// ── After-sales summary banner ────────────────────────────────────────────────

function AfterSalesBanner({ afterSales }) {
  if (!afterSales) return null;

  const { sale_amount, paid_amount, balance_amount, payment_status } = afterSales;

  const statusCfg = {
    paid:    { color: "success", label: "Paid" },
    partial: { color: "warning", label: "Partially Paid" },
    pending: { color: "error",   label: "Pending" },
  }[payment_status] ?? { color: "info", label: payment_status ?? "Unknown" };

  return (
    <Alert
      type={statusCfg.color}
      showIcon
      icon={<DollarOutlined />}
      style={{ marginBottom: 12, padding: "6px 12px" }}
      message={
        <div style={{ display: "flex", gap: 20, fontSize: 12, flexWrap: "wrap" }}>
          <span>
            <span style={{ color: "#8c8c8c" }}>Sale: </span>
            <strong>AED {Number(sale_amount ?? 0).toFixed(2)}</strong>
          </span>
          <span>
            <span style={{ color: "#8c8c8c" }}>Paid: </span>
            <strong>AED {Number(paid_amount ?? 0).toFixed(2)}</strong>
          </span>
          <span>
            <span style={{ color: "#8c8c8c" }}>Balance: </span>
            <strong>AED {Number(balance_amount ?? 0).toFixed(2)}</strong>
          </span>
          <Tag
            color={statusCfg.color === "success" ? "green" : statusCfg.color === "warning" ? "orange" : "red"}
            style={{ fontSize: 10, margin: 0 }}
          >
            {statusCfg.label}
          </Tag>
        </div>
      }
    />
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function providerKey(tab) {
  return tab === "network" ? "network" : "mamopay";
}

function latestForTab(rows, tab) {
  const key = providerKey(tab);
  return rows.find(r => r.provider_name === key && r.payment_link_url) ?? null;
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export function PaymentLinkModal({ open, onClose, card, userId, authState }) {
  const headers = useMemo(() => ({ accessToken: localStorage.getItem("accessToken") }), []);
  const { socket } = useSocket();

  // Data fetch state
  const [parts, setParts] = useState([]);
  const [costPrices, setCostPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [afterSales, setAfterSales] = useState(null);

  // Pricing state
  const [delivery, setDelivery] = useState(0);
  const [vatPct, setVatPct] = useState(DEFAULT_VAT_PCT);
  const [discount, setDiscount] = useState(0);
  const [discountReason, setDiscountReason] = useState("");

  // Provider tab + per-tab fee
  const [activeTab, setActiveTab] = useState("network");
  const [networkFeePct, setNetworkFeePct] = useState(NETWORK_FEE_PCT);
  const [mamoFeePct, setMamoFeePct] = useState(MAMO_FEE_PCT);
  const feePct = activeTab === "network" ? networkFeePct : mamoFeePct;
  const setFeePct = activeTab === "network" ? setNetworkFeePct : setMamoFeePct;

  // Network-specific customer details
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [skipEmail, setSkipEmail] = useState(false);

  // Link generation result
  const [generatedPayment, setGeneratedPayment] = useState(null); // { id, payment_link_url }
  const [generating, setGenerating] = useState(false);
  const linkRef = useRef(null);

  useEffect(() => {
    if (generatedPayment?.payment_link_url && linkRef.current) {
      setTimeout(() => linkRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 80);
    }
  }, [generatedPayment?.payment_link_url]);

  const selectedParts = useMemo(
    () => parts.filter(p => p.selected_quote_id && p.selling_price != null),
    [parts]
  );
  const partsTotal = useMemo(
    () => selectedParts.reduce((s, p) => s + Number(p.selling_price), 0),
    [selectedParts]
  );

  const customerPhone = card?.customer?.customer_mobile || card?.customer_mobile || null;
  const customerName = card?.customer?.customer_name || card?.customer_name || "";

  // Fetch parts + payment history on open
  useEffect(() => {
    if (!open || !card?.id) {
      setParts([]);
      setGeneratedPayment(null);
      setAfterSales(null);
      setDiscount(0);
      setDiscountReason("");
      return;
    }
    setLoading(true);
    setGeneratedPayment(null);

    axios
      .get(`${API_DOMAIN}/query_workflow/task/get?query_pk=${card.id}`, { headers })
      .then(res => {
        if (res.data.success) {
          const taskData = res.data.data ?? {};
          const taskObj = taskData.task ?? taskData;
          const rawParts = taskData.parts ?? taskObj.parts ?? [];
          const mapped = rawParts.map(p => ({
            ...p,
            supplier_quotes: p.supplier_quotes ?? p.quotes ?? [],
          }));
          setParts(mapped);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    setHistoryLoading(true);
    axios
      .get(`${API_DOMAIN}/queries/${card.id}/payments`, { headers })
      .then(res => {
        if (res.data.success) {
          const d = res.data.data ?? {};
          const rows = d.payments ?? [];
          setHistory(rows);
          setAfterSales(d.after_sales ?? null);
          // Pre-populate inline display with the most recent link
          const latest = rows[0];
          if (latest?.payment_link_url) {
            const tab = latest.provider_name === "network" ? "network" : "mamo";
            setActiveTab(tab);
            setGeneratedPayment(latest);
          }
        }
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));

    // Pre-fill customer name for Network
    const nameParts = customerName.trim().split(/\s+/);
    setFirstName(nameParts[0] || "");
    setLastName(nameParts.slice(1).join(" ") || "");
    setEmail("");
    setSkipEmail(false);
  }, [open, card?.id]); // eslint-disable-line

  // Fetch cost prices once, then pre-populate delivery
  useEffect(() => {
    if (!open) return;
    fetchCostPricesOnce().then(data => {
      setCostPrices(data);
    });
  }, [open]);

  // Auto-set delivery from cost tier when parts + cost prices are ready
  useEffect(() => {
    if (!open || !parts.length || !costPrices.length) return;
    const d = computeDelivery(parts, costPrices);
    setDelivery(d);
  }, [parts, costPrices, open]);

  const refreshHistory = (tabOverride) => {
    if (!card?.id) return;
    setHistoryLoading(true);
    axios
      .get(`${API_DOMAIN}/queries/${card.id}/payments`, { headers })
      .then(res => {
        if (res.data.success) {
          const d = res.data.data ?? {};
          const rows = d.payments ?? [];
          setHistory(rows);
          setAfterSales(d.after_sales ?? null);
          const tab = tabOverride ?? activeTab;
          setGeneratedPayment(latestForTab(rows, tab));
        }
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  };

  // ── Socket listeners ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket || !open || !card?.id) return;
    const qpk = card.id;

    const onPaymentUpdated = (data) => {
      if (data?.data?.payment?.query_pk !== qpk) return;
      const updated = data.data.payment;
      setHistory(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
      setGeneratedPayment(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
    };

    const onAfterSalesUpdated = (data) => {
      if (data?.data?.query_pk !== qpk) return;
      setAfterSales(data.data);
    };

    const onWebhookProcessed = (data) => {
      if (data?.data?.payment?.query_pk !== qpk) return;
      const { payment, after_sales, event_type } = data.data;
      if (after_sales) setAfterSales(prev => ({ ...prev, ...after_sales }));
      if (payment) {
        setHistory(prev => prev.map(r => r.id === payment.id ? { ...r, ...payment } : r));
        setGeneratedPayment(prev => prev?.id === payment.id ? { ...prev, ...payment } : prev);
      }
      if (event_type === "CAPTURED" || payment?.payment_status === "received") {
        message.success({ content: "Payment received from customer!", duration: 5 });
      }
    };

    socket.on("query:payment_updated", onPaymentUpdated);
    socket.on("query:after_sales_updated", onAfterSalesUpdated);
    socket.on("query:payment_webhook_processed", onWebhookProcessed);
    return () => {
      socket.off("query:payment_updated", onPaymentUpdated);
      socket.off("query:after_sales_updated", onAfterSalesUpdated);
      socket.off("query:payment_webhook_processed", onWebhookProcessed);
    };
  }, [socket, open, card?.id]); // eslint-disable-line

  const { vatAmount, processingFeeAmount, finalPayable } = calcTotals({ partsTotal, delivery, vatPct, feePct, discount });

  const buildPricingSnapshot = () => {
    const deliveryShare = selectedParts.length > 0
      ? Math.round((Number(delivery) / selectedParts.length) * 100) / 100
      : 0;
    return {
      parts_total: partsTotal,
      delivery_amount: Number(delivery),
      vat_pct: vatPct,
      vat_amount: vatAmount,
      processing_fee_pct: feePct,
      processing_fee_amount: processingFeeAmount,
      discount: Number(discount),
      final_payable: finalPayable,
      parts_breakdown: selectedParts.map(p => {
        const quote = (p.supplier_quotes ?? p.quotes ?? []).find(q => q.id === p.selected_quote_id)
          ?? p.selected_quote_summary ?? null;
        const sp = Number(p.selling_price);
        const cp = Number(quote?.quoted_price || 0);
        const finalPrice = Math.round((sp + deliveryShare) * (1 + vatPct / 100) * 100) / 100;
        return {
          query_part_id: p.id,
          part_name: p.part_name || "",
          included: true,
          sp,
          cp,
          quantity: p.quantity || 1,
          condition: p.condition_preference || p.condition || p.part_condition || "New",
          warranty_days: Number(p.warranty_days || quote?.warranty_days || 0),
          vat_enabled: true,
          vat_pct: vatPct,
          delivery_share: deliveryShare,
          final_price: finalPrice,
        };
      }),
    };
  };

  const INACTIVE_LINK_STATUSES = new Set(["received", "failed", "cancelled", "expired", "refunded", "disabled"]);

  const doGenerateLink = async () => {
    setGenerating(true);
    try {
      const snapshot = buildPricingSnapshot();
      const body = {
        userId,
        query_id: card.query_id,
        provider_name: activeTab === "network" ? "network" : "mamopay",
        amount: finalPayable,
        payment_stage: "full_payment",
        discount_amount: Number(discount),
        discount_reason: discountReason || null,
        pricing_snapshot: snapshot,
        title: `Payment for Query ${card.query_id}`,
        description: "Customer payment link",
        enable_tabby: false,
        ...(activeTab === "network" && {
          customer_email: skipEmail ? null : email.trim(),
          customer_details: {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: skipEmail ? null : email.trim(),
          },
        }),
      };
      const res = await axios.post(
        `${API_DOMAIN}/queries/${card.id}/payments/create-link`,
        body,
        { headers }
      );
      if (!res.data.success) throw new Error(res.data.message);
      const { payment, payment_link_url } = res.data.data;
      setGeneratedPayment({ ...payment, payment_link_url });
      message.success("Payment link created");
      refreshHistory();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "Failed to create payment link");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateLink = () => {
    if (selectedParts.length === 0) {
      message.warning("No selected parts found for this query.");
      return;
    }
    if (activeTab === "network") {
      if (!firstName.trim()) { message.warning("Enter customer first name"); return; }
      if (!lastName.trim()) { message.warning("Enter customer last name"); return; }
      if (!skipEmail && !email.trim()) { message.warning("Enter email or check 'Skip email'"); return; }
    }

    const existingActiveLink = history.find(
      r => r.payment_method === "pay_link" && !INACTIVE_LINK_STATUSES.has(r.payment_status)
    );

    if (existingActiveLink) {
      Modal.confirm({
        title: "Active payment link already exists",
        content: (
          <div style={{ fontSize: 13 }}>
            <p style={{ marginBottom: 8 }}>
              There is already an active payment link for this task:
            </p>
            <div style={{ background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6, padding: "8px 12px", marginBottom: 12 }}>
              <div><strong>AED {Number(existingActiveLink.amount || 0).toFixed(2)}</strong></div>
              {existingActiveLink.payment_link_url && (
                <div style={{ fontSize: 11, color: "#595959", wordBreak: "break-all", marginTop: 4 }}>
                  {existingActiveLink.payment_link_url}
                </div>
              )}
              <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 4 }}>
                Status: {existingActiveLink.payment_status || "pending"}
              </div>
            </div>
            <p style={{ color: "#d46b08", margin: 0 }}>
              Creating a new link will <strong>not</strong> cancel or expire the old one — both will remain active until paid or expired by the provider.
            </p>
          </div>
        ),
        okText: "Create new link anyway",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: doGenerateLink,
        width: 440,
      });
      return;
    }

    doGenerateLink();
  };

  const networkTabContent = (
    <div>
      <PricingSnapshot
        selectedParts={selectedParts}
        partsTotal={partsTotal}
        delivery={delivery} setDelivery={setDelivery}
        vatPct={vatPct} setVatPct={setVatPct}
        feePct={feePct} setFeePct={setFeePct}
        discount={discount} setDiscount={setDiscount}
        discountReason={discountReason} setDiscountReason={setDiscountReason}
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>First Name *</div>
          <Input
            placeholder="First name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Last Name *</div>
          <Input
            placeholder="Last name"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>Email</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input
            placeholder="Customer email"
            value={email}
            disabled={skipEmail}
            onChange={e => setEmail(e.target.value)}
          />
          <Checkbox checked={skipEmail} onChange={e => setSkipEmail(e.target.checked)}>
            <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>Skip email</span>
          </Checkbox>
        </div>
      </div>
      {generatedPayment?.payment_link_url && (
        <div ref={linkRef}>
          <LinkDisplay
            link={generatedPayment.payment_link_url}
            paymentId={generatedPayment.id}
            userId={userId}
            customerPhone={customerPhone}
            sentAt={generatedPayment.link_sent_at ?? null}
            onSent={(updatedPayment) => {
              setGeneratedPayment(prev => ({ ...prev, ...updatedPayment }));
              refreshHistory();
              onClose();
            }}
          />
        </div>
      )}
    </div>
  );

  const mamoTabContent = (
    <div>
      <PricingSnapshot
        selectedParts={selectedParts}
        partsTotal={partsTotal}
        delivery={delivery} setDelivery={setDelivery}
        vatPct={vatPct} setVatPct={setVatPct}
        feePct={feePct} setFeePct={setFeePct}
        discount={discount} setDiscount={setDiscount}
        discountReason={discountReason} setDiscountReason={setDiscountReason}
      />
      {generatedPayment?.payment_link_url && (
        <div ref={linkRef}>
          <LinkDisplay
            link={generatedPayment.payment_link_url}
            paymentId={generatedPayment.id}
            userId={userId}
            customerPhone={customerPhone}
            sentAt={generatedPayment.link_sent_at ?? null}
            onSent={(updatedPayment) => {
              setGeneratedPayment(prev => ({ ...prev, ...updatedPayment }));
              refreshHistory();
              onClose();
            }}
          />
        </div>
      )}
    </div>
  );

  const tabItems = [
    {
      key: "network",
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <img src={network_logo} alt="Network" style={{ width: 36, height: 20, objectFit: "contain", borderRadius: 2 }} />
          Network
        </span>
      ),
      children: loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin /></div>
      ) : networkTabContent,
    },
    {
      key: "mamo",
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <img src={mamo_logo} alt="Mamo Pay" style={{ width: 36, height: 20, objectFit: "contain", borderRadius: 2 }} />
          Mamo Pay
        </span>
      ),
      children: loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin /></div>
      ) : mamoTabContent,
    },
    {
      key: "history",
      label: (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <HistoryOutlined />
          History
          {history.length > 0 && (
            <span style={{
              background: "#1677ff", color: "#fff", borderRadius: 8,
              padding: "0 5px", fontSize: 10, fontWeight: 700, lineHeight: "16px",
            }}>
              {history.length}
            </span>
          )}
        </span>
      ),
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <Button size="small" icon={<ReloadOutlined />} loading={historyLoading} onClick={refreshHistory}>
              Refresh
            </Button>
          </div>
          <PaymentHistory
            history={history}
            loading={historyLoading}
            customerPhone={customerPhone}
            customerName={customerName}
          />
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={`Payment — #${card?.query_id || ""}`}
      open={open}
      onCancel={onClose}
      centered
      width={560}
      maskClosable={false}
      styles={{ body: { overflowY: "auto", maxHeight: "calc(90vh - 160px)", paddingRight: 4 } }}
      footer={
        activeTab !== "history" ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>
              {selectedParts.length} part{selectedParts.length !== 1 ? "s" : ""} · AED {finalPayable.toFixed(2)} payable
            </span>
            <Button
              type="primary"
              icon={<LinkOutlined />}
              loading={generating}
              onClick={handleGenerateLink}
            >
              Create Payment Link
            </Button>
          </div>
        ) : null
      }
    >
      <AfterSalesBanner afterSales={afterSales} />
      <Tabs
        activeKey={activeTab}
        onChange={(k) => { setActiveTab(k); setGeneratedPayment(latestForTab(history, k)); }}
        items={tabItems}
        size="small"
      />
    </Modal>
  );
}
